import { FormProvider, useForm, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { UserAvatarTooltip } from "@/components/avatar-user-tooltip"
import { Banner } from "@/components/ui/banner"
import { CardHeader } from "@/components/ui/card"
import { UserProvider } from "@/components/user-wrapper"
import { formatDateString } from "@/lib/format-date"
import { EpisodeOfCareDropdown } from "@/modules/patients/detail/episode-of-care/episode-of-care-dropdown"
import { getEpisodeOfCareFragment } from "@/modules/patients/detail/episode-of-care/lib/get-episode-of-care-fragment"
import {
  EpisodeOfCareFragment$data,
  EpisodeOfCareFragment$key,
} from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type"
import { ChatTranscriptContainer } from "@/modules/patients/detail/progress-note/components/chat-transcript-container"
import { formDirtySchema } from "@/modules/patients/detail/progress-note/components/progress-note-editable/treatment-plan-section"
import { useAssignEpisodeOfCareForm } from "@/modules/patients/detail/progress-note/hooks/useAssignEpisodeOfCareForm"
import { useAssignEpisodeOfCareFormEffect } from "@/modules/patients/detail/progress-note/hooks/useAssignEpisodeOfCareFormEffect"
import { useIsProgressNoteEditable } from "@/modules/patients/detail/progress-note/hooks/useIsProgressNoteEditable"
import { CallerRoleIcon } from "@/modules/patients/detail/progress-note/lib/get-caller-role-icon"
import { getProgressNoteChannel } from "@/modules/patients/detail/progress-note/lib/get-progress-note-channel"
import { getProgressNoteTitle } from "@/modules/patients/detail/progress-note/lib/get-progress-note-title"
import { ProgressNoteActions } from "@/modules/patients/detail/progress-note/progress-note-actions"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { Country } from "@/types/__generated__/union-types"

interface ProgressNoteCardHeaderProps {
  isEditMode?: boolean
  isEpisodeOfCareUpdatable?: boolean
  parentEpisodeOfCareFragmentReference: EpisodeOfCareFragment$key
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  country: Country
  isEditWithin24HoursAfterCloseMode: boolean
  onEditWithin24HoursAfterCloseModeChange: (value: boolean) => void
}

const assignEpisodeOfCareSchema = z
  .object({
    icdCode: z.string().refine((value) => value !== "dummy", {
      message: "episodeOfCare.progressNote.progressNoteMissingEpisodeOfCare",
    }),
  })
  .extend(formDirtySchema.shape)

export type AssignEpisodeOfCareFormType = z.infer<typeof assignEpisodeOfCareSchema>

const ProgressNoteCardHeader = ({
  isEditMode,
  isEpisodeOfCareUpdatable,
  parentEpisodeOfCareFragmentReference,
  episodeOfCareProgressNoteData,
  country,
  isEditWithin24HoursAfterCloseMode,
  onEditWithin24HoursAfterCloseModeChange,
}: ProgressNoteCardHeaderProps) => {
  const patientId = usePatientParam()
  const { t } = useTranslation()

  const isProgressNoteEditable = useIsProgressNoteEditable(episodeOfCareProgressNoteData)

  const {
    id: progressNoteId,
    appointment: {
      id: appointmentId,
      service,
      hasChatTranscript,
      appointmentType,
      specialty,
      channel,
      callerRole,
      requestType,
      access,
      suggestedQuestionnaireDiagnosisCodes,
    },
    createdAt,
    createdBy,
  } = episodeOfCareProgressNoteData

  const episodeOfCare = useFragment(getEpisodeOfCareFragment(country), parentEpisodeOfCareFragmentReference)

  const { defaultValues, storeFormState } = useAssignEpisodeOfCareForm(
    progressNoteId,
    episodeOfCare.icdDiagnosis.diagnosisCode,
  )

  const form = useForm<AssignEpisodeOfCareFormType>({
    resolver: zodResolver(assignEpisodeOfCareSchema),
    defaultValues: defaultValues,
  })

  useAssignEpisodeOfCareFormEffect(
    progressNoteId,
    appointmentId,
    episodeOfCareProgressNoteData,
    storeFormState,
    form,
    isProgressNoteEditable,
    isEditWithin24HoursAfterCloseMode,
  )

  return (
    <FormProvider {...form}>
      <CardHeader
        className="flex min-w-106.75 items-center justify-between gap-4 border-b py-3 pr-2 pl-4"
        data-testid="progress-note-header">
        <div className="flex items-center gap-2 lg:gap-4">
          <UserProvider
            userName={createdBy}
            render={(user) => <UserAvatarTooltip user={user} uniqueIdentifier={progressNoteId} size="md" />}
          />
          <div className="grid gap-1">
            <div className="flex items-center gap-1">
              <div className="grid w-fit max-w-100 min-w-0">
                <span className="text-on-surface truncate text-base font-medium">
                  {getProgressNoteTitle(t, appointmentType, specialty, service)} {t("episodeOfCare.progressNote.for")}
                </span>
              </div>
              <div className="grid truncate text-left">
                {isEditMode && isEpisodeOfCareUpdatable ? (
                  <EpisodeOfCareDropdown
                    progressNoteId={progressNoteId}
                    episodeOfCare={episodeOfCare}
                    country={country}
                    channel={channel}
                    requestType={requestType}
                    suggestedQuestionnaireDiagnosisCodes={suggestedQuestionnaireDiagnosisCodes}
                  />
                ) : (
                  <EpisodeOfCareText episodeOfCare={episodeOfCare} />
                )}
              </div>
            </div>
            <div className="text-on-surface-subdued -mt-2 flex items-center gap-1 text-xs">
              {formatDateString(createdAt)}
              {hasChatTranscript && <ChatTranscriptContainer patientId={patientId} appointmentId={appointmentId} />}
              <span>{" • "}</span>
              {getProgressNoteChannel(channel)}
              <CallerRoleIcon callerRole={callerRole} />
              <span>{" • "}</span>
              {t(`scheduling.waitingRoom.access.${access}`)}
            </div>
          </div>
        </div>
        <ProgressNoteActions
          isEpisodeOfCareUpdatable={isEpisodeOfCareUpdatable}
          parentEpisodeOfCareFragmentReference={parentEpisodeOfCareFragmentReference}
          episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
          isEditWithin24HoursAfterCloseMode={isEditWithin24HoursAfterCloseMode}
          onEditWithin24HoursAfterCloseModeChange={onEditWithin24HoursAfterCloseModeChange}
        />
      </CardHeader>
      <EpisodeOfCareInvalidBanner isEditMode={isEditMode} isProgressNoteEditable={isProgressNoteEditable} />
    </FormProvider>
  )
}

const EpisodeOfCareText = ({ episodeOfCare }: { episodeOfCare: EpisodeOfCareFragment$data }) => {
  const { t } = useTranslation()
  return (
    <div className="text-on-surface truncate text-base font-medium">
      {episodeOfCare
        ? episodeOfCare.isVirtual
          ? t("episodeOfCare.progressNote.notAllocatedEoc")
          : episodeOfCare.icdDiagnosis.text
        : t("episodeOfCare.progressNote.unassignedEpisodeOfCare")}
    </div>
  )
}

const EpisodeOfCareInvalidBanner = ({
  isEditMode,
  isProgressNoteEditable,
}: {
  isEditMode?: boolean
  isProgressNoteEditable: boolean
}) => {
  const { t } = useTranslation()

  const {
    formState: { errors },
  } = useFormContext<AssignEpisodeOfCareFormType>()

  return (
    isEditMode &&
    errors.icdCode?.message &&
    isProgressNoteEditable && (
      <Banner className="px-4" variant="critical" data-validation-error="">
        {t("episodeOfCare.progressNote.progressNoteMissingEpisodeOfCare")}
      </Banner>
    )
  )
}

export { ProgressNoteCardHeader }
